import { useQuery } from '@tanstack/react-query';

import type {
  ActionLogResponse, ModelLogResponse, RequestLogResponse, ResponseLogResponse
} from '@/api';
import APIGateway from '@/api';
import type { SortOrder } from '@/types/logs';

export type TraceRequestFilters = {
  agentName?: string;
  constellationName?: string;
  constellationVersion?: string;
  clientId?: string;
  sessionId?: string;
  requestId?: string;
  correlationId?: string;
  startTime?: string;
  endTime?: string;
  sortOrder?: SortOrder;
  sortBy?: string;
};

export const traceKeys = {
  all: [ 'traces' ] as const,
  requests: ( agentName: string, batchSize: number, offset: number, filters?: TraceRequestFilters ) => [
    ...traceKeys.all, 'requests', agentName, batchSize, offset, filters
  ] as const,
  requestsByRequestId: ( requestId: string ) => [
    ...traceKeys.all, 'requestsByRequestId', requestId
  ] as const,
  traceModels: ( agentName: string | undefined, requestId: string ) => [
    ...traceKeys.all, 'models', agentName ?? '__any__', requestId
  ] as const,
  traceActions: ( agentName: string | undefined, requestId: string ) => [
    ...traceKeys.all, 'actions', agentName ?? '__any__', requestId
  ] as const,
  traceResponses: ( callId: string ) => [
    ...traceKeys.all, 'responses', callId
  ] as const
};

/** Fetch the list of completion requests for the request list panel.
 *  `agentName` is sent as the `agent_id` filter; empty/undefined means
 *  "across all agents" — the caller is expected to supply other filters in
 *  that case. */
export function useTraceRequestsQuery(
  agentName: string | undefined,
  batchSize: number = 20,
  offset: number = 0,
  filters?: TraceRequestFilters
) {
  return useQuery( {
    queryKey: traceKeys.requests( agentName ?? '', batchSize, offset, filters ),
    queryFn: async(): Promise<RequestLogResponse> => {
      return APIGateway.logs.requestLogsGet( {
        agent_id: agentName,
        agent_name: filters?.agentName,
        constellation_name: filters?.constellationName,
        constellation_version: filters?.constellationVersion,
        client_id: filters?.clientId,
        session_id: filters?.sessionId,
        batch_size: batchSize,
        offset,
        request_id: filters?.requestId,
        correlation_id: filters?.correlationId,
        from_start_time: filters?.startTime,
        to_end_time: filters?.endTime,
        sort_order: filters?.sortOrder,
        sort_by: filters?.sortBy
      } );
    },
    staleTime: 30000,
    retry: 1
  } );
}

/** Fetch model logs (private, with messages) for a specific request. When
 *  `agentName` is undefined, returns logs across all agents that share the
 *  request_id — used to discover subagent calls. */
export function useTraceModelLogsQuery(
  agentName: string | undefined,
  requestId: string | null
) {
  return useQuery( {
    queryKey: traceKeys.traceModels( agentName, requestId! ),
    queryFn: async(): Promise<ModelLogResponse> => {
      if ( !requestId ) throw new Error( 'Request ID required' );
      return APIGateway.logs.modelLogsGet( { agent_id: agentName, batch_size: 100, request_id: requestId } );
    },
    enabled: !!requestId,
    staleTime: 60000,
    retry: 1
  } );
}

/** Fetch action logs for a specific request. When `agentName` is undefined,
 *  returns logs across all agents that share the request_id. */
export function useTraceActionLogsQuery(
  agentName: string | undefined,
  requestId: string | null
) {
  return useQuery( {
    queryKey: traceKeys.traceActions( agentName, requestId! ),
    queryFn: async(): Promise<ActionLogResponse> => {
      if ( !requestId ) throw new Error( 'Request ID required' );
      return APIGateway.logs.actionLogsGet( { agent_id: agentName, batch_size: 100, request_id: requestId } );
    },
    enabled: !!requestId,
    staleTime: 60000,
    retry: 1
  } );
}

/** Fetch all completion requests sharing a request_id, across all agents.
 *  Used to discover subagent invocations that propagated the parent request_id. */
export function useTraceRequestsByRequestIdQuery( requestId: string | null ) {
  return useQuery( {
    queryKey: traceKeys.requestsByRequestId( requestId! ),
    queryFn: async(): Promise<RequestLogResponse> => {
      if ( !requestId ) throw new Error( 'Request ID required' );
      return APIGateway.logs.requestLogsGet( { batch_size: 100, request_id: requestId } );
    },
    enabled: !!requestId,
    staleTime: 60000,
    retry: 1
  } );
}

/** Fetch completion responses for a specific call_id */
export function useTraceResponsesQuery( callId: string | null ) {
  return useQuery( {
    queryKey: traceKeys.traceResponses( callId! ),
    queryFn: async(): Promise<ResponseLogResponse> => {
      if ( !callId ) throw new Error( 'Call ID required' );
      return APIGateway.logs.responseLogsGet( callId );
    },
    enabled: !!callId,
    staleTime: 60000,
    retry: 1
  } );
}
